import React, { useEffect, type ReactElement } from "react"
import { Route, Routes, Navigate } from "react-router-dom"
import NotFound from "../pages/NotFound/404Erro"
import { useLoad } from "../context/useLoadContext"
import Load from "../components/Load/Load"
import { useError } from "../context/useErrorContext"
import ErrorPopup from "../components/ErrorPopup/ErrorPopup"
import { HomePage } from "../pages/HomePage/HomePage"
import { PATH_MAP } from "../configuration/PathMap"
import { useEmployeeContext } from "../context/useMenuEmployeeContext"
import { EmployeePupop } from "../pages/MyStore/Employees/EmployeePupop"
import { useLogout } from "../hooks/useLogout"
import { useConfirmation } from "../context/useConfirmationContext"
import { API_ROUTES } from "../configuration/RoutesApi"
import LandingPage from "../pages/LandPage/LandPage"
import HowItWorksPage from "../pages/LandPage/HowItWorksPage"
import PublicTutorialsPage from "../pages/LandPage/PublicTutorialsPage"
import DigitalMenuLandingPage from "../pages/LandPage/DigitalMenuLandingPage"
import FreeMenuSetupLandingPage from "../pages/LandPage/FreeMenuSetupLandingPage"
import AudienceLandingPage from "../pages/LandPage/AudienceLandingPage"
import LeadQuizLandingPage from "../pages/LandPage/LeadQuizLandingPage"
import SignUpFinal from "../pages/SignUp/SignUpFinal"
import TermsOfService from "../pages/TermsOfService/TermsOfService"
import ResetPasswordPage from "../pages/ResetPasswordPage/ResetPasswordPage"
import MessagePopup from "../components/MessagePopup/MessagePopup"
import { useMessage } from "../context/useMessageContext"
import { ConfirmationPopup } from "../components/ConfirmationPopup/ConfirmationPopup"
import PublicPage from "../PublicMenu/Pages/PublicPage"
import LoginPage from "../pages/LoginPage/LoginPage"
import { CourierPage } from "../pages/Delivery/CourierPage"
import PrivacyPolicy from "../pages/TermsOfService/PrivacyPolicy"
import RefundPolicy from "../pages/TermsOfService/RefundPolicy"
import CancellationPolicy from "../pages/TermsOfService/CancellationPolicy"
import { useUpgradePlan } from "../context/useUpgradePlanContext"
import { PlanPopup } from "../pages/MyStore/MyCompany/PlanPopup"
import StoreSearchPage from "../pages/StoreSearchPage/StoreSearchPage"
import { SeoRouteGuard } from "../seo/SeoRouteGuard"
import AdminOnlyRoute from "./AdminOnlyRoute"
import { AdminLoginPage } from "../pages/Admin/AdminLoginPage"
import { AdminDashboardScreen } from "../pages/Admin/AdminDashboardScreen"
import ResellerOnlyRoute from "./ResellerOnlyRoute"
import { ResellerLandingPage } from "../pages/LandPage/ResellerLandingPage"
import { ResellerSignUpPage } from "../pages/Reseller/ResellerSignUpPage"
import { ResellerLoginPage } from "../pages/Reseller/ResellerLoginPage"
import { ResellerDashboardScreen } from "../pages/Reseller/ResellerDashboardScreen"
import { RedirectPaymentModal } from "../PublicMenu/Components/RedirectPaymentModal"
import { useRedirectPayment } from "../context/useRedirectPayment"
import { StoreGroupPublicPage } from "../pages/StoreGroupPublic/StoreGroupPublicPage"
import FulfillmentOnlyRoute from "./FulfillmentOnlyRoute"
import { FulfillmentPage } from "../pages/FulfillmentPage/FulfillmentPage"
import ServicePointSelfServicePage from "../PublicMenu/Pages/ServicePointSelfServicePage"
import { TimeClockPublicCodePage } from "../pages/TimeClock/TimeClockPublicCodePage"
import { defaultMarketingQuizSlug } from "../configuration/MarketingQuizzes"

export const RoutesConfig: React.FC = () => {
  const { isLoading } = useLoad()

  const { isOpenPlan, closePlan, planUsageStatus } = useUpgradePlan()

  const { hasError, messageError, titleError, disableError } = useError()

  const { hasMessage, message, titleMessage, disableMessage } = useMessage()

  const { hasNeedConfirmation, messageConfirmation, titleConfirmation, disable, action } = useConfirmation()

  const { handleCloseEmployee, employee, isOpen, title, isCreateMaster } = useEmployeeContext()

  const { isRedirectingToPayment } = useRedirectPayment()

  const { checkTokenUser } = useLogout()

  const isLoggedIn = localStorage.getItem(API_ROUTES.TOKEN) !== null

  checkTokenUser()

  const protectedRoute = (children: ReactElement, path: string) => {
    if (!isLoggedIn && path !== PATH_MAP.ROOT_PAGE && path !== PATH_MAP.ROOT_LOGIN) {
      return <Navigate to={PATH_MAP.ROOT_PAGE} replace />
    }

    if (isLoggedIn && (path == PATH_MAP.ROOT_PAGE || path == PATH_MAP.ROOT_LOGIN)) {
      return <Navigate to={PATH_MAP.COMMANDS_PAGE} replace />
    }

    return children
  }

  useEffect(() => {
    const handleCloseOnboardingPopups = () => handleCloseEmployee()

    window.addEventListener("close-owner-onboarding-popups", handleCloseOnboardingPopups)

    return () => window.removeEventListener("close-owner-onboarding-popups", handleCloseOnboardingPopups)
  }, [handleCloseEmployee])

  return (
    <>
      {isRedirectingToPayment && <RedirectPaymentModal />}

      {isLoading && <Load />}

      {isOpenPlan && <PlanPopup onClose={closePlan} planUsageStatus={planUsageStatus} />}

      {hasError && <ErrorPopup title={titleError} message={messageError} onClose={disableError} />}

      {hasMessage && <MessagePopup title={titleMessage} message={message} onClose={disableMessage} />}

      {isOpen && (
        <EmployeePupop
          employee={employee}
          isCreateMaster={isCreateMaster}
          title={title}
          onClonse={handleCloseEmployee}
        />
      )}

      {hasNeedConfirmation && (
        <ConfirmationPopup
          title={titleConfirmation}
          message={messageConfirmation}
          onClose={disable}
          onConfirmation={action!}
        />
      )}

      <SeoRouteGuard />

      <Routes>
        <Route path={PATH_MAP.ADMIN_LOGIN_PAGE} element={<AdminLoginPage />} />

        <Route
          path={PATH_MAP.ADMIN_DASHBOARD_PAGE}
          element={
            <AdminOnlyRoute>
              <AdminDashboardScreen />
            </AdminOnlyRoute>
          }
        />

        <Route path={PATH_MAP.COURIER_PANEL_PAGE} element={<CourierPage />} />

        <Route path={PATH_MAP.PRIVACY_POLICY} element={<PrivacyPolicy />} />

        <Route path={PATH_MAP.REFUND_POLICY} element={<RefundPolicy />} />

        <Route path={PATH_MAP.CANCELLATION_POLICY} element={<CancellationPolicy />} />

        <Route path={PATH_MAP.STORE_GROUP_PUBLIC_PAGE} element={<StoreGroupPublicPage />} />

        <Route path={PATH_MAP.SERVICE_POINT_SELF_SERVICE} element={<ServicePointSelfServicePage />} />

        <Route path={PATH_MAP.TIME_CLOCK_PUBLIC_CODE_PAGE} element={<TimeClockPublicCodePage />} />

        <Route path={PATH_MAP.PUBLIC_BASE} element={<PublicPage />} />

        <Route
          path={PATH_MAP.FULFILLMENT_PAGE}
          element={protectedRoute(
            <FulfillmentOnlyRoute>
              <FulfillmentPage station="FULFILLMENT" />
            </FulfillmentOnlyRoute>,
            PATH_MAP.FULFILLMENT_PAGE,
          )}
        />

        <Route
          path={PATH_MAP.SECONDARY_FULFILLMENT_PAGE}
          element={protectedRoute(
            <FulfillmentOnlyRoute>
              <FulfillmentPage station="SECONDARY" />
            </FulfillmentOnlyRoute>,
            PATH_MAP.SECONDARY_FULFILLMENT_PAGE,
          )}
        />

        <Route path={PATH_MAP.ROOT_LOGIN} element={protectedRoute(<LoginPage />, PATH_MAP.ROOT_LOGIN)} />

        <Route path={PATH_MAP.RECOVER_PASSWORD} element={<ResetPasswordPage />} />

        <Route path={PATH_MAP.TERMS_OF_SERVICE} element={<TermsOfService />} />

        <Route path={PATH_MAP.ROOT_SIGN_UP_FIRST_STEP} element={<Navigate to={PATH_MAP.ROOT_SIGN_UP} replace />} />

        <Route path={PATH_MAP.ROOT_SIGN_UP_SECOND_STEP} element={<Navigate to={PATH_MAP.ROOT_SIGN_UP} replace />} />

        <Route path={PATH_MAP.ROOT_SIGN_UP} element={<SignUpFinal />} />

        <Route path={PATH_MAP.HOW_IT_WORKS_PAGE} element={<HowItWorksPage />} />

        <Route path={PATH_MAP.PUBLIC_TUTORIALS_PAGE} element={<PublicTutorialsPage />} />

        <Route path={PATH_MAP.STORE_SEARCH_PAGE} element={<StoreSearchPage />} />

        <Route path={PATH_MAP.DIGITAL_MENU_LANDING_PAGE} element={<DigitalMenuLandingPage />} />

        <Route path={PATH_MAP.FREE_MENU_SETUP_LANDING_PAGE} element={<FreeMenuSetupLandingPage />} />

        <Route path={PATH_MAP.RESELLER_LANDING_PAGE} element={<ResellerLandingPage />} />

        <Route path={PATH_MAP.RESELLER_SIGNUP_PAGE} element={<ResellerSignUpPage />} />

        <Route path={PATH_MAP.RESELLER_LOGIN_PAGE} element={<ResellerLoginPage />} />

        <Route
          path={PATH_MAP.RESELLER_DASHBOARD_PAGE}
          element={
            <ResellerOnlyRoute>
              <ResellerDashboardScreen />
            </ResellerOnlyRoute>
          }
        />

        <Route
          path={PATH_MAP.LEAD_QUIZ_PAGE}
          element={<Navigate to={PATH_MAP.LEAD_QUIZ_LINK(defaultMarketingQuizSlug)} replace />}
        />

        <Route path={PATH_MAP.LEAD_QUIZ_SLUG_PAGE} element={<LeadQuizLandingPage />} />

        <Route path={PATH_MAP.AUDIENCE_LANDING_PAGE} element={<AudienceLandingPage />} />

        <Route path={PATH_MAP.CREATE_VIRTUAL_STORE_PAGE} element={<LandingPage />} />

        <Route path={PATH_MAP.FREE_VIRTUAL_STORE_PAGE} element={<LandingPage />} />

        <Route path={PATH_MAP.SELL_ONLINE_PAGE} element={<LandingPage />} />

        <Route path={PATH_MAP.ECOMMERCE_PLATFORM_PAGE} element={<LandingPage />} />

        <Route path={PATH_MAP.ROOT_PAGE} element={protectedRoute(<LandingPage />, PATH_MAP.ROOT_PAGE)} />

        <Route path={`${PATH_MAP.HOME_PAGE}/*`} element={protectedRoute(<HomePage />, PATH_MAP.HOME_PAGE)} />

        <Route path={PATH_MAP.ROOT_ALL} element={<NotFound />} />
      </Routes>
    </>
  )
}
